import { ArrowRight, Check, ClipboardText, Star } from "@phosphor-icons/react";
import type { PrybarComponentActions, PrybarComponentRenderer } from "@prybar/core";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { askPrybar } from "./spark";

const CARD = "rounded-2xl border border-border-card bg-surface-elevated p-4 shadow-card";
const EYEBROW = "font-sans text-xs font-medium text-ink-eyebrow";

function Eyebrow({ children }: { children: string }) {
  return <span className={EYEBROW}>{children}</span>;
}

interface Pillar {
  score: number;
  label: string;
  status: string;
}

interface SnapshotResult {
  score: number | null;
  pillars: Record<string, Pillar>;
  activeMicrosteps: number;
  doneToday: number;
  briefHeadline: string | null;
}

function PillarBars({ pillars }: { pillars: Record<string, Pillar> }) {
  return (
    <div className="mt-3 space-y-1.5">
      {Object.values(pillars ?? {}).map((p) => (
        <div key={p.label} className="flex items-center gap-2">
          <span className="w-20 shrink-0 text-xs text-ink-primary/65">{p.label}</span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-track">
            <div
              className={
                p.status === "steady"
                  ? "h-full rounded-full bg-teal-500"
                  : "h-full rounded-full bg-orange-400"
              }
              style={{ width: `${Math.max(4, Math.min(100, p.score))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function SnapshotCard({ data }: { data: SnapshotResult }) {
  return (
    <div className={CARD}>
      <div className="flex items-baseline justify-between">
        <Eyebrow>Today's wellbeing</Eyebrow>
        <span className="text-sm text-ink-primary/60">
          {data.doneToday}/{data.activeMicrosteps} done
        </span>
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="font-sans text-[34px] font-medium leading-none text-ink-primary">
          {data.score ?? "n/a"}
        </span>
        <span className="text-sm text-ink-primary/55">
          {data.score == null ? "nothing graded yet" : "Snapshot score"}
        </span>
      </div>
      <PillarBars pillars={data.pillars} />
    </div>
  );
}

interface ScoreResult {
  score: number | null;
  pillars: Record<string, Pillar>;
  sleep: {
    avgHours: number;
    deltaHours: number;
    nightsAtTarget: number;
    targetHours: number;
    status: string;
  } | null;
}

function ScoreBreakdownCard({ data }: { data: ScoreResult }) {
  const { sleep } = data;
  return (
    <div className={CARD}>
      <Eyebrow>Score breakdown</Eyebrow>
      <PillarBars pillars={data.pillars} />
      {sleep && (
        <div className="mt-3 border-t border-border-default pt-3 text-sm text-ink-primary/70">
          <span className="font-medium text-ink-primary">{sleep.avgHours.toFixed(1)}h</span> average
          sleep, {sleep.nightsAtTarget} night
          {sleep.nightsAtTarget === 1 ? "" : "s"} at your {sleep.targetHours}h target
        </div>
      )}
    </div>
  );
}

interface BriefResult {
  hasBrief: boolean;
  title?: string | null;
  subtitle?: string | null;
  summary?: string | null;
  sources?: string[];
  hasAudio?: boolean;
}

function BriefCard({ data }: { data: BriefResult }) {
  if (!data.hasBrief) {
    return (
      <div className={CARD}>
        <Eyebrow>Weekly brief</Eyebrow>
        <p className="mt-2 text-sm text-ink-primary/70">No brief yet this week.</p>
      </div>
    );
  }
  return (
    <div className={CARD}>
      <Eyebrow>Weekly brief</Eyebrow>
      <p className="mt-1.5 font-title text-[18px] font-medium leading-snug text-ink-primary">
        {data.title}
      </p>
      {data.summary && (
        <p className="mt-1.5 text-sm leading-relaxed text-ink-primary/70">{data.summary}</p>
      )}
      {data.sources && data.sources.length > 0 && (
        <p className="mt-2 font-sans font-medium text-xs text-ink-quaternary">
          Sources: {data.sources.join(" · ")}
        </p>
      )}
    </div>
  );
}

interface ConnectionsResult {
  count: number;
  connections: { provider: string; synced: boolean; lastSyncAt: string | null }[];
}

function ConnectionsCard({ data }: { data: ConnectionsResult }) {
  return (
    <div className={CARD}>
      <Eyebrow>Connectors</Eyebrow>
      {data.connections.length === 0 ? (
        <p className="mt-2 text-sm text-ink-primary/70">Nothing connected yet.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {data.connections.map((c) => (
            <li key={c.provider} className="flex items-center justify-between text-sm">
              <span className="capitalize text-ink-primary">{c.provider}</span>
              <span
                className={
                  c.synced
                    ? "rounded-full bg-surface-accent-leaf px-2 py-0.5 text-xs font-medium text-ink-accent-leaf"
                    : "rounded-full bg-surface-track px-2 py-0.5 text-xs font-medium text-ink-tertiary"
                }
              >
                {c.synced ? "Synced" : "Connected"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface FeedbackResult {
  subject: string;
}

interface SurveyPromptResult {
  surveyId: string;
  route: string | null;
  title: string;
  questionSet: string;
  surfaceLabel: string;
  ratingPrompt: string;
  freeformPrompt: string;
  bugPrompt: string;
  bugTextPrompt: string;
  questions: { id: string; prompt: string }[];
}

interface SurveyPickerOption {
  surveyId: string;
  title: string;
  questionSet: string;
  surfaceLabel: string;
  description: string;
  kind: "current_page" | "post_onboarding" | "general";
}

interface SurveyPickerResult {
  route: string | null;
  options: SurveyPickerOption[];
}

const FEEDBACK_OPTIONS: { value: "up" | "down"; label: string }[] = [
  { value: "up", label: "Looks right" },
  { value: "down", label: "Not right" },
];

function FeedbackCard({
  data,
  actions,
}: {
  data: FeedbackResult;
  actions: PrybarComponentActions;
}) {
  const [chosen, setChosen] = useState<"up" | "down" | null>(null);
  const submit = (sentiment: "up" | "down") => {
    setChosen(sentiment);
    void actions.callTool("feedback.submit", { about: data.subject, sentiment });
  };
  return (
    <div className={CARD}>
      <p className="text-sm font-medium text-ink-primary">
        How is <span className="text-ink-brand">{data.subject}</span> for you?
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {FEEDBACK_OPTIONS.map((o) => (
          <button
            type="button"
            key={o.value}
            disabled={chosen !== null}
            onClick={() => submit(o.value)}
            className={`rounded-full border px-3 py-1.5 text-sm transition ${
              chosen === o.value
                ? "border-teal-600 bg-teal-600 text-ink-on-brand"
                : "border-teal-200 bg-surface-secondary text-teal-800 hover:bg-teal-100 disabled:opacity-50"
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>
      {chosen && (
        <p className="mt-2 text-sm text-ink-brand">
          {chosen === "down"
            ? "Noted. Tell me what is off and I will record it."
            : "Noted, thanks."}
        </p>
      )}
    </div>
  );
}

function SurveyCard({
  data,
  actions,
}: {
  data: SurveyPromptResult;
  actions: PrybarComponentActions;
}) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [rating, setRating] = useState<number | null>(null);
  const [freeformText, setFreeformText] = useState("");
  const [bugExperienced, setBugExperienced] = useState(false);
  const [bugText, setBugText] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const hasQuestionAnswer = data.questions.some((question) => answers[question.id]?.trim());
  const hasBugText = bugExperienced && bugText.trim().length > 0;
  const canSubmit =
    rating !== null || freeformText.trim().length > 0 || hasQuestionAnswer || hasBugText;
  const submit = async () => {
    if (!canSubmit || status === "saving") return;
    setStatus("saving");
    try {
      await actions.callTool("survey.submit", {
        answers,
        bugExperienced,
        bugText,
        freeformText,
        rating,
        route: data.route,
        surveyId: data.surveyId,
      });
      setStatus("saved");
    } catch {
      setStatus("error");
    }
  };
  return (
    <div className={CARD}>
      <div className="flex items-baseline justify-between gap-3">
        <Eyebrow>{data.title}</Eyebrow>
        <span className="font-sans font-medium text-xs text-ink-quaternary">
          {data.questions.length} questions
        </span>
      </div>
      <p className="mt-1.5 font-title text-[18px] font-medium leading-snug text-ink-primary">
        {data.questionSet}
      </p>
      <fieldset className="mt-4 rounded-lg border border-border-default bg-surface-secondary/60 p-3">
        <legend className="text-sm font-medium text-ink-primary">{data.ratingPrompt}</legend>
        <div className="mt-2 flex items-center gap-1.5">
          {[1, 2, 3, 4, 5].map((value) => (
            <label key={value} className="inline-flex">
              <input
                type="radio"
                name={`survey-rating-${data.surveyId}`}
                value={value}
                checked={rating === value}
                aria-label={`${value} out of 5`}
                disabled={status === "saving" || status === "saved"}
                onChange={() => setRating(value)}
                className="peer sr-only"
              />
              <span
                className="grid h-8 w-8 place-items-center rounded-md text-ink-brand transition hover:bg-surface-elevated peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-teal-500 peer-disabled:opacity-60"
                aria-hidden="true"
              >
                <Star size={18} weight={rating !== null && value <= rating ? "fill" : "regular"} />
              </span>
            </label>
          ))}
        </div>
      </fieldset>
      <div className="mt-4 space-y-4">
        {data.questions.map((question, index) => (
          <label key={question.id} className="block">
            <span className="font-sans text-xs font-medium text-ink-eyebrow">
              Question {index + 1}
            </span>
            <span className="mt-1 block text-sm leading-relaxed text-ink-primary/75">
              {question.prompt}
            </span>
            <textarea
              value={answers[question.id] ?? ""}
              onChange={(event) => {
                setAnswers((prev) => ({ ...prev, [question.id]: event.target.value }));
                if (status === "error") setStatus("idle");
              }}
              disabled={status === "saving" || status === "saved"}
              rows={3}
              className="mt-2 min-h-[82px] w-full resize-y rounded-md border border-border-strong/50 bg-surface-elevated px-3 py-2 text-sm leading-relaxed text-ink-primary outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:opacity-60"
            />
          </label>
        ))}
        <label className="block">
          <span className="font-sans text-xs font-medium text-ink-eyebrow">Notes</span>
          <span className="mt-1 block text-sm leading-relaxed text-ink-primary/75">
            {data.freeformPrompt}
          </span>
          <textarea
            value={freeformText}
            onChange={(event) => {
              setFreeformText(event.target.value);
              if (status === "error") setStatus("idle");
            }}
            disabled={status === "saving" || status === "saved"}
            rows={3}
            className="mt-2 min-h-[82px] w-full resize-y rounded-md border border-border-strong/50 bg-surface-elevated px-3 py-2 text-sm leading-relaxed text-ink-primary outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:opacity-60"
          />
        </label>
        <div className="rounded-lg border border-border-default bg-surface-elevated p-3">
          <button
            type="button"
            aria-pressed={bugExperienced}
            disabled={status === "saving" || status === "saved"}
            onClick={() => setBugExperienced((value) => !value)}
            className={`flex w-full items-center justify-between gap-3 text-left text-sm font-medium transition ${
              bugExperienced ? "text-teal-800" : "text-ink-primary"
            } disabled:opacity-60`}
          >
            <span>{data.bugPrompt}</span>
            <span
              aria-hidden="true"
              className={`grid h-5 w-5 shrink-0 place-items-center rounded-sm border ${
                bugExperienced
                  ? "border-teal-700 bg-teal-700 text-ink-on-brand"
                  : "border-border-strong/60 bg-surface-elevated"
              }`}
            >
              {bugExperienced ? <Check size={12} weight="bold" /> : null}
            </span>
          </button>
          {bugExperienced && (
            <label className="mt-3 block">
              <span className="font-sans text-xs font-medium text-ink-eyebrow">Details</span>
              <span className="mt-1 block text-sm leading-relaxed text-ink-primary/75">
                {data.bugTextPrompt}
              </span>
              <textarea
                value={bugText}
                onChange={(event) => {
                  setBugText(event.target.value);
                  if (status === "error") setStatus("idle");
                }}
                disabled={status === "saving" || status === "saved"}
                rows={3}
                className="mt-2 min-h-[82px] w-full resize-y rounded-md border border-border-strong/50 bg-surface-elevated px-3 py-2 text-sm leading-relaxed text-ink-primary outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-100 disabled:opacity-60"
              />
            </label>
          )}
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <p aria-live="polite" className="text-sm text-ink-primary/55">
          {status === "saved"
            ? "Recorded, thank you."
            : status === "error"
              ? "Something went wrong. Try again."
              : "Choose a rating or leave a note."}
        </p>
        <button
          type="button"
          disabled={!canSubmit || status === "saving" || status === "saved"}
          onClick={() => void submit()}
          className="rounded-md border border-teal-700 bg-teal-700 px-3 py-1.5 text-sm font-medium text-ink-on-brand transition hover:bg-teal-800 disabled:cursor-not-allowed disabled:border-border-strong/50 disabled:bg-surface-track disabled:text-ink-quaternary"
        >
          {status === "saving" ? "Saving" : status === "saved" ? "Saved" : "Submit"}
        </button>
      </div>
    </div>
  );
}

function optionLabel(kind: SurveyPickerOption["kind"]): string {
  if (kind === "current_page") return "Current page";
  if (kind === "post_onboarding") return "Post-onboarding";
  return "General";
}

function SurveyPickerCard({ data }: { data: SurveyPickerResult }) {
  const openSurvey = (option: SurveyPickerOption) => {
    const args: Record<string, unknown> = { surveyId: option.surveyId };
    if (data.route) args.route = data.route;
    askPrybar(`Open feedback for ${option.surfaceLabel}.`, {
      directExec: true,
      toolCallHint: { name: "survey.prompt", args },
    });
  };
  return (
    <div className={CARD}>
      <Eyebrow>Feedback</Eyebrow>
      <p className="mt-1.5 font-title text-[18px] font-medium leading-snug text-ink-primary">
        Choose a survey
      </p>
      <div className="mt-3 space-y-2">
        {data.options.map((option) => (
          <button
            type="button"
            key={option.surveyId}
            onClick={() => openSurvey(option)}
            className="flex w-full items-start gap-3 rounded-lg border border-border-card bg-surface-elevated px-3 py-2.5 text-left transition hover:border-border-primary hover:bg-surface-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500"
          >
            <span
              aria-hidden="true"
              className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-md bg-[color-mix(in_srgb,var(--page-accent,var(--color-teal-600))_16%,transparent)] text-[var(--page-accent,var(--color-ink-brand))]"
            >
              <ClipboardText size={17} weight="regular" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="font-sans text-xs font-medium text-ink-eyebrow">
                {optionLabel(option.kind)}
              </span>
              <span className="mt-0.5 block text-sm font-medium text-ink-primary">
                {option.questionSet}
              </span>
              <span className="mt-0.5 block text-sm leading-relaxed text-ink-primary/60">
                {option.description}
              </span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

interface LibraryItem {
  id: string;
  kind: string;
  title: string;
  source: string;
}
interface LibraryResult {
  count: number;
  items: LibraryItem[];
}

function LibraryCard({ data }: { data: LibraryResult }) {
  const items = data.items ?? [];
  return (
    <div className={CARD}>
      <Eyebrow>Explore</Eyebrow>
      {items.length === 0 ? (
        <p className="mt-2 text-sm text-ink-primary/70">No matching content found.</p>
      ) : (
        <ul className="mt-2.5 space-y-1.5">
          {items.map((it, i) => (
            <li
              key={it.id ?? `${it.title}-${i}`}
              className="flex items-center justify-between gap-2.5 text-sm"
            >
              <span className="truncate text-ink-primary">{it.title}</span>
              <span className="shrink-0 rounded-full bg-surface-track px-2 py-0.5 font-sans font-medium text-xs text-ink-eyebrow">
                {it.kind}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface ContentPayload {
  matched?: boolean;
  contentId: string;
  kind: string;
  title: string;
  summary: string | null;
  imageUrl: string | null;
  route: string;
}

const CONTENT_KIND_LABELS: Record<string, string> = {
  Reset: "Reset",
  Recipe: "Recipe",
  Article: "Article",
  Story: "Story",
  RoleModel: "Story",
  PodcastEpisode: "Podcast",
  Guide: "Guide",
};

const CONTENT_OPEN_LABELS: Record<string, string> = {
  Reset: "Watch the Reset",
  Guide: "Watch the Guide",
  PodcastEpisode: "Listen",
  Recipe: "Open the recipe",
};

/**
 * One recommended piece of content, image-led like the Heartbeat pick on
 * Explore. Opening goes through the same `/explore?open=` route the app uses,
 * so the Explore page hands the item to its own player or sheet.
 */
function ContentCard({ item, eyebrow }: { item: ContentPayload; eyebrow: string }) {
  const navigate = useNavigate();
  const openLabel = CONTENT_OPEN_LABELS[item.kind] ?? "Open";
  const safeRoute = item.route.startsWith("/") && !item.route.startsWith("//") ? item.route : null;
  return (
    <div className="overflow-hidden rounded-2xl border border-border-card bg-surface-elevated shadow-card">
      {item.imageUrl ? (
        <div className="aspect-[16/9] w-full overflow-hidden bg-surface-track">
          <img
            src={item.imageUrl}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover object-top"
          />
        </div>
      ) : null}
      <div className="p-4">
        <div className="flex items-center justify-between gap-3">
          <Eyebrow>{eyebrow}</Eyebrow>
          <span className="rounded-full bg-surface-secondary px-2 py-0.5 text-xs font-medium text-teal-800">
            {CONTENT_KIND_LABELS[item.kind] ?? item.kind}
          </span>
        </div>
        <p className="mt-1.5 font-title text-[19px] font-medium leading-snug text-ink-primary">
          {item.title}
        </p>
        {item.summary ? (
          <p className="mt-1.5 line-clamp-3 text-sm leading-relaxed text-ink-primary/70">
            {item.summary}
          </p>
        ) : null}
        {safeRoute ? (
          <button
            type="button"
            onClick={() => navigate(safeRoute)}
            className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-teal-600 px-3.5 py-1.5 text-sm font-semibold text-ink-on-brand transition hover:bg-teal-700"
          >
            {openLabel}
            <ArrowRight size={14} weight="bold" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

interface MemoryResult {
  success: boolean;
  kind?: "fact" | "habit";
  label?: string;
  category?: string;
  updatedExisting?: boolean;
  forgot?: string;
}

/** Nothing about the member is saved or dropped silently: the write shows up as a card. */
function MemoryCard({
  action,
  data,
}: {
  action: "remember" | "update" | "forget";
  data: MemoryResult;
}) {
  const title =
    action === "forget"
      ? `Forgot ${data.forgot ?? "that"}`
      : `${action === "update" || data.updatedExisting ? "Updated" : "Remembered"}: ${data.label ?? "that"}`;
  return (
    <div className={CARD}>
      <Eyebrow>What I know about you</Eyebrow>
      <p className="mt-1.5 font-title text-[18px] font-medium leading-snug text-ink-primary">
        {title}
      </p>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="rounded-full bg-surface-secondary px-2 py-0.5 text-xs font-medium text-teal-800">
          {data.kind === "habit" ? "Habit" : "About you"}
        </span>
        {data.category ? (
          <span className="rounded-full bg-surface-track px-2 py-0.5 text-xs font-medium text-ink-secondary">
            {data.category}
          </span>
        ) : null}
      </div>
      <p className="mt-2 text-xs text-ink-tertiary">
        {action === "forget"
          ? "It no longer shapes what I suggest."
          : "It will shape what I suggest. Ask me to forget it at any time."}
      </p>
    </div>
  );
}

const memorySucceeded = (result: unknown) =>
  Boolean((result as { success?: boolean } | undefined)?.success);

export const healthOsRenderers: PrybarComponentRenderer[] = [
  {
    name: "healthos.memory.remember",
    match: (toolName, result) => toolName === "memory.remember" && memorySucceeded(result),
    render: (data) => <MemoryCard action="remember" data={data as MemoryResult} />,
  },
  {
    name: "healthos.memory.update",
    match: (toolName, result) => toolName === "memory.update" && memorySucceeded(result),
    render: (data) => <MemoryCard action="update" data={data as MemoryResult} />,
  },
  {
    name: "healthos.memory.forget",
    match: (toolName, result) => toolName === "memory.forget" && memorySucceeded(result),
    render: (data) => <MemoryCard action="forget" data={data as MemoryResult} />,
  },
  {
    name: "healthos.content.recommend",
    match: (toolName, result) =>
      toolName === "content.recommend" &&
      Boolean((result as { found?: boolean; item?: unknown } | undefined)?.item),
    render: (data) => {
      const item = (data as { item: ContentPayload }).item;
      return (
        <ContentCard
          item={item}
          eyebrow={item.matched === false ? "Closest I could find" : "Recommended for you"}
        />
      );
    },
  },
  {
    name: "healthos.content.open",
    match: (toolName, result) =>
      toolName === "content.open" &&
      Boolean((result as { success?: boolean; item?: unknown } | undefined)?.item),
    render: (data) => (
      <ContentCard item={(data as { item: ContentPayload }).item} eyebrow="Opening for you" />
    ),
  },
  {
    name: "healthos.today.snapshot",
    match: (toolName) => toolName === "today.snapshot",
    render: (data) => <SnapshotCard data={data as SnapshotResult} />,
  },
  {
    name: "healthos.score.breakdown",
    match: (toolName) => toolName === "score.breakdown",
    render: (data) => <ScoreBreakdownCard data={data as ScoreResult} />,
  },
  {
    name: "healthos.brief.read",
    match: (toolName) => toolName === "brief.read",
    render: (data) => <BriefCard data={data as BriefResult} />,
  },
  {
    name: "healthos.connections.status",
    match: (toolName) => toolName === "connections.status",
    render: (data) => <ConnectionsCard data={data as ConnectionsResult} />,
  },
  {
    name: "healthos.library.list",
    match: (toolName) => toolName === "library.list",
    render: (data) => <LibraryCard data={data as LibraryResult} />,
  },
  {
    name: "healthos.feedback.prompt",
    match: (toolName, result) =>
      toolName === "feedback.prompt" &&
      Boolean((result as { _prybar_feedback_prompt?: boolean })?._prybar_feedback_prompt),
    render: (data, _args, actions) => (
      <FeedbackCard data={data as FeedbackResult} actions={actions} />
    ),
  },
  {
    name: "healthos.survey.picker",
    match: (toolName, result) =>
      toolName === "survey.picker" &&
      Boolean((result as { _prybar_survey_picker?: boolean })?._prybar_survey_picker),
    render: (data) => <SurveyPickerCard data={data as SurveyPickerResult} />,
  },
  {
    name: "healthos.survey.prompt",
    match: (toolName, result) =>
      toolName === "survey.prompt" &&
      Boolean((result as { _prybar_survey_prompt?: boolean })?._prybar_survey_prompt),
    render: (data, _args, actions) => (
      <SurveyCard data={data as SurveyPromptResult} actions={actions} />
    ),
  },
];
